import type {
  CurrencyCode,
  HistoryPoint,
  MarketData,
  MetalHistoryResponse,
  MetalRate,
  MetalSymbol,
  PriceChange,
  WeightUnit,
} from "./types";

const TROY_OUNCE_GRAMS = 31.1034768;

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });
  const body = (await response.json()) as T & { message?: string };
  if (!response.ok) {
    const error = new Error(
      response.status === 401
        ? "Bitte erneut anmelden."
        : "Marktdaten konnten nicht vollständig geladen werden.",
    ) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return body;
}

export async function loadMarketData(): Promise<MarketData> {
  const [latest, forex, historyResult] = await Promise.all([
    getJson<MarketData["latest"]>("/api/metals/latest"),
    getJson<MarketData["forex"]>("/api/forex/latest"),
    getJson<MetalHistoryResponse>("/api/metals/history")
      .then((value) => ({ ok: true as const, value }))
      .catch(() => ({ ok: false as const })),
  ]);
  return {
    latest,
    forex,
    history: historyResult.ok ? historyResult.value : null,
  };
}

export function currencyRate(data: MarketData, currency: CurrencyCode): number {
  if (currency === "EUR") return 1;
  return data.forex.rates.find((rate) => rate.symbol === currency)?.rate ?? 0;
}

export function convertFromEur(
  value: number,
  data: MarketData,
  currency: CurrencyCode,
): number {
  return value * currencyRate(data, currency);
}

export function calculateChange(
  history: MetalHistoryResponse | null,
  symbol: MetalSymbol,
): PriceChange | null {
  const values = (history?.points ?? [])
    .map((point) => point[symbol])
    .filter((value): value is number => typeof value === "number");
  if (values.length < 2 || values[0] === 0) return null;
  const absolute = values[values.length - 1] - values[0];
  return {
    absolute,
    percentage: (absolute / values[0]) * 100,
    high: Math.max(...values),
    low: Math.min(...values),
  };
}

export function chartSeries(
  history: MetalHistoryResponse | null,
  symbol: MetalSymbol,
  data: MarketData,
  currency: CurrencyCode,
): Array<{ date: string; price: number }> {
  return (history?.points ?? []).flatMap((point: HistoryPoint) =>
    typeof point[symbol] === "number"
      ? [
          {
            date: point.date,
            price: convertFromEur(point[symbol] as number, data, currency),
          },
        ]
      : [],
  );
}

export function performanceSeries(
  history: MetalHistoryResponse | null,
): Array<HistoryPoint & Partial<Record<MetalSymbol, number>>> {
  const points = history?.points ?? [];
  const symbols: MetalSymbol[] = ["XAU", "XAG", "XPT", "XPD"];
  const base = Object.fromEntries(
    symbols.map((symbol) => [
      symbol,
      points.find((point) => typeof point[symbol] === "number")?.[symbol],
    ]),
  ) as Partial<Record<MetalSymbol, number>>;

  return points.map((point) => {
    const normalized: HistoryPoint & Partial<Record<MetalSymbol, number>> = {
      date: point.date,
    };
    symbols.forEach((symbol) => {
      const value = point[symbol];
      const first = base[symbol];
      if (typeof value === "number" && typeof first === "number" && first !== 0)
        normalized[symbol] = (value / first - 1) * 100;
    });
    return normalized;
  });
}

export function metalValue(
  rate: MetalRate,
  weight: number,
  unit: WeightUnit,
  data: MarketData,
  currency: CurrencyCode,
): number {
  if (!Number.isFinite(weight) || weight < 0) return 0;
  const ounces =
    unit === "ounce"
      ? weight
      : unit === "kilogram"
        ? (weight * 1000) / TROY_OUNCE_GRAMS
        : weight / TROY_OUNCE_GRAMS;
  return convertFromEur(rate.ounce * ounces, data, currency);
}

export function formatMoney(
  value: number,
  currency: CurrencyCode,
  digits = 2,
): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString("de-DE");
}
export function marketIsStale(timestamp: number): boolean {
  return Date.now() - timestamp * 1000 > 10 * 60 * 1000;
}
